import type { ORG_ONBOARDING_STORAGE_KEYS } from './use-organization-onboarding';

/**
 * The decision behind the server step 1 of /getting-started: does this visitor go through
 * today's flow instead (a code in the URL, a stored session, saved progress)? It runs as an
 * inline script while the HTML is parsed, before the first paint, so it lives in a module with
 * no runtime imports. See `server-step-one.tsx` for the whole picture.
 */

type OrgOnboardingStorageKey = (typeof ORG_ONBOARDING_STORAGE_KEYS)[keyof typeof ORG_ONBOARDING_STORAGE_KEYS];

/** Class set on `<html>` by the inline script when this visitor must not see the server step 1. */
export const SERVER_STEP_ONE_HOLD_CLASS = 'rw-gs-hold';

/** What the inline script needs to decide, passed in as data so the function stays self-contained. */
export const SERVER_STEP_ONE_HOLD_INPUT = {
  codeParams: ['ref', 'partner'],
  tokenKey: 'jwt',
  // Saved progress that changes what the page opens on: a later step, the resume modal, the
  // referral or partner intro, or the return from the Flow 3 login.
  // Written out rather than read from ORG_ONBOARDING_STORAGE_KEYS so this module imports nothing
  // at runtime; the type below still fails the build if one of them stops being a real key.
  progressKeys: [
    'org-onboarding-restaurant',
    'org-onboarding-jp-data',
    'org-onboarding-contact',
    'org-onboarding-existing-user-flow',
    'org-onboarding-referral-info',
    'org-onboarding-partner-info',
  ] satisfies OrgOnboardingStorageKey[],
};

export type ServerStepOneHoldInput = typeof SERVER_STEP_ONE_HOLD_INPUT;

/**
 * True when the visitor must not see the server step 1.
 *
 * Serialised with `toString()` into the inline script, so it must not reference anything outside
 * its own body: no imports, no helpers, no syntax the compiler would rewrite into a helper call
 * (spread, optional chaining, `for…of`). Plain `for` loops and `let`/`const` only.
 */
export function shouldHoldServerStepOne(input: ServerStepOneHoldInput): boolean {
  const search = window.location.search;
  for (let i = 0; i < input.codeParams.length; i++) {
    if (new RegExp('[?&]' + input.codeParams[i] + '=').test(search)) return true;
  }

  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch (e) {
    storage = null;
  }
  // No storage (privacy modes, some WebViews): no session and no progress can be stored either.
  if (!storage) return false;

  const token = storage.getItem(input.tokenKey);
  if (token) {
    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const exp = JSON.parse(window.atob(payload)).exp;
      // An expired token is a signed-out visitor: auth will discard it.
      if (!exp || exp * 1000 > Date.now()) return true;
    } catch (e) {
      // Unreadable token: let auth decide, as today.
      return true;
    }
  }

  for (let k = 0; k < input.progressKeys.length; k++) {
    const value = storage.getItem(input.progressKeys[k]);
    if (value !== null && value !== 'null' && value !== 'false' && value !== '') return true;
  }
  return false;
}

/** The script, as it goes into the HTML. Never throws: a failure means the shell stays visible. */
export const SERVER_STEP_ONE_HOLD_SCRIPT = `(function(){try{if((${shouldHoldServerStepOne.toString()})(${JSON.stringify(
  SERVER_STEP_ONE_HOLD_INPUT
)}))document.documentElement.classList.add('${SERVER_STEP_ONE_HOLD_CLASS}')}catch(e){}})();`;

/** Whether the inline script hid the shell for this visitor. Client only. */
export const isServerStepOneHeld = (): boolean =>
  typeof document !== 'undefined' && document.documentElement.classList.contains(SERVER_STEP_ONE_HOLD_CLASS);
